import PageTableHeader from "../../../components/pageTableHeader/pageTableHeader";
import CommonTable from "../../../components/table/table";
import BottomDrawer from "../../common/drawer/bottomDrawer";
import {
  useCommonFetch,
  usePageFilter,
  useFilterOptions,
} from "../../../../hooks";
import { useEffect, useState } from "react";
import CommonForm from "../../../components/form/form";
import {
  createDesignation,
  deleteDesignation,
  getDesignationList,
  updateDesignation,
} from "../../../../services/workspace/designation.service";
import toast from "react-hot-toast";
import { convertToLabelValue } from "../../../../utils/common";
import { getRoleList } from "../../../../services/workspace/role.service";
import {
  commonConditions,
  createActionButtons,
} from "../../../../utils/actionButtonHelpers";

export const actions_type = {
  DELETE: "DELETE",
  ADD_EDIT: "ADD_EDIT",
};

const TableHeaderData = [
  {
    label: "Designation Name",
    key: "name",
  },
  {
    label: "Department",
    key: "department",
  },
  {
    label: "Description",
    key: "description",
  },
  {
    label: "Created At",
    key: "created_at",
  },
];

export const items = [{ label: "Manage Designation", path: "" }];

const ManageDesignation = () => {
  const [isEditValue, setIsEditValue] = useState(null);
  const [roles, setRoles] = useState();

  // ✨ Auto fetch and cache department options from Redux!
  // No more repeated API calls - data fetched once and stored!
  const { departments, departmentsLoading, refresh } = useFilterOptions([
    "departments",
  ]);

  // Fetch roles (you can add this to useFilterOptions too!)
  const fetchRoles = () => {
    getRoleList({})
      .then((response) => {
        const roleData = response?.role?.data || [];
        const formattedRoles = convertToLabelValue(roleData);
        setRoles(formattedRoles || []);
      })
      .catch((error) => {
        toast.error("Failed to fetch roles data!");
      });
  };

  // Fetch data with useCommonFetch
  const {
    loading,
    data: tableData,
    total: totalData,
    currentPage,
    search,
    selectedId,
    action,
    handleSearchChange,
    handlePageChange,
    openModal,
    closeModal,
    handleCreate,
    handleUpdate,
    handleDelete,
    sortingUIProps,
    applyFilters,
    clearFilters,
  } = useCommonFetch(getDesignationList, {
    dataPath: "designation.data",
    totalPath: "designation.meta.total",
    errorMessage: "Failed to fetch designation data!",
    debounceDelay: 500,
    filterModule: "designation", // Add filter module name for Redux persistence
    customParams: {
      with: "department,roles",
    },
    sortingOptions: [
      {
        label: "Date",
        field: "created_at",
        buttonText: { ascending: "Oldest First", descending: "Newest First" },
      },
      {
        label: "Name",
        field: "name",
        buttonText: { ascending: "A-Z", descending: "Z-A" },
      },
    ],
    sortingModule: "designation",
    orderParamNames: {
      orderByKey: "order_by",
      orderTypeKey: "order_type",
      includeDisabledKey: "includeDisabled",
    },
    sortingDisableText: "Disabled",
    sortingShowDisableToggle: false,
    // Add data transformer function

    dataTransformer: (data) => {
      if (Array.isArray(data)) {
        const transformedData = data.map((item, index) => ({
          ...item,
          // Add custom fields or modify existing ones
          id: {
            data: item.uuid,
            className: "",
            sticky: false,
            variant: "text",
            customCases: null,
          },
          name: {
            data: item.name,
            className: "",
            sticky: false,
            variant: "name",
            customCases: null,
          },
          abrv: {
            data: item.abrv,
            className: "",
            variant: "text",
            customCases: null,
          },
          department: {
            data: item.department || null,
            className: "",
            sticky: false,
            variant: "select",
            options: departments,
            customCases: null,
          },
          roles: {
            data: item.roles,
            className: "",
            sticky: false,
            variant: "select",
            options: roles,
            customCases: null,
          },
          description: {
            data: item.description,
            className: "",
            sticky: false,
            variant: "text",
            customCases: null,
          },
          created_at: {
            data: item.created_at,
            className: "",
            sticky: false,
            variant: "date-time",
            customCases: null,
          },
          // Add any other transformations you need
          status: {
            data: item.status ? 1 : 0,
            className: "",
            sticky: false,
            variant: "status",
            customCases: null,
          },
          actions: {
            data: item.id,
            className: "",
            sticky: false,
            variant: "text",
            customCases: null,
          },
        }));

        const actionButton = createActionButtons(
          {
            delete: {
              handler: (item) => {
                handleDelete(item.id.data, deleteDesignation, {
                  successMessage: "Designation deleted successfully!",
                  errorMessage: "Failed to delete designation!",
                });
              },
              // show:true // Show delete only for non-system generated items
              show: commonConditions.nonSystemGenerated, // Show delete only for non-system generated items
            },
            edit: {
              handler: (item) => {
                openModal(item, actions_type.ADD_EDIT);
                const InitialData = {
                  id: item.id.data,
                  abrv: item.abrv.data,
                  name: item?.name.data,
                  department_id: item.department?.data?.id || item.department?.id || null,
                  roles: Array.isArray(item.roles?.data)
                    ? item.roles.data.map((role) => role?.id).filter(Boolean)
                    : item.roles?.data?.id
                    ? [item.roles.data.id]
                    : [],
                  description: item.description?.data || "",
                };
                setIsEditValue(InitialData);
              },
              show: commonConditions.nonSystemGenerated, // Show edit only for non-system generated items
            },
          },
          {
            systemGeneratedBadge: true, // Enable system generated badge
            badgeText: "System Generated",
            badgeVariant: "info",
          }
        );

        return {
          data: transformedData,
          action: actionButton,
        };
      }
      return { data: [], action: {} };
    },
  });

  // ✨ Single hook for ALL filter setup - define everything ONCE!
  const { filterProps } = usePageFilter(
    "designation", // Module name (only once!)
    applyFilters, // From useCommonFetch
    clearFilters, // From useCommonFetch
    {
      filters: ["department"], // Filter names (only once!)
      options: { departmentOptions: departments }, // ✨ From Redux cache!
      onRefreshOptions: () => refresh("departments"), // ✨ Refresh callback for manual refresh!
    }
  );

  // Fetch roles on mount (departments auto-fetched by useFilterOptions)
  useEffect(() => {
    fetchRoles();
  }, []);

  // Comprehensive form fields
  const comprehensiveFields = [
    // Basic text fields
    {
      name: "name",
      label: "Designation Name",
      type: "text",
      placeholder: "Enter designation name",
      required: true,
      minLength: 2,
      maxLength: 40,
    },
    {
      name: "abrv",
      label: "Abbreviation",
      type: "text",
      placeholder: "Enter abbreviation name",
      required: true,
    },
    {
      name: "department_id",
      label: "Department",
      type: "select",
      options: departments, // ✨ From Redux cache
      required: true,
      disabled: departmentsLoading, // ✨ Disable jab tak load ho raha hai
      placeholder: departmentsLoading
        ? "Loading departments..."
        : "Select department",
      onRefresh: () => refresh("departments"), // ✨ Refresh button ke liye
    },
    {
      name: "roles",
      label: "Permission",
      type: "select",
      options: roles,
      required: true,
      multiple: true,
    },

    // Textarea
    {
      name: "description",
      label: "Description",
      type: "textarea",
      // required: true,
      placeholder: "Write description here...",
      maxLength: 300,
    },
  ];

  // Handle form submission
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    // Edit
    if (isEditValue) {
      await handleUpdate(values, isEditValue.id, updateDesignation, {
        successMessage: "Designation updated successfully!",
        errorMessage: "Failed to update designation!",
      });
    } else {
      // Create new
      await handleCreate(values, createDesignation, {
        successMessage: "Designation created successfully!",
        errorMessage: "Failed to create designation!",
      });
    }

    // alert(`Form submitted successfully in ${testMode} mode!`);
    setSubmitting(false);
    // Reset form after submission
    setTimeout(() => {
      resetForm();
      setIsEditValue();
      // setFormValues({});
    }, 1000);
  };

  // Handle form cancellation
  const handleCancel = () => {

    setIsEditValue(null);
    closeModal();
    // setFormValues({});
    // setTestMode('create');
  };

  // Handle drawer close
  const handleDrawerClose = (isOpen) => {
    if (!isOpen) {
      handleCancel();
    }
  };

  return (
    <div className={"flex flex-col gap-2"}>
      {/* ✨ Show loading indicator jab departments load ho rahe hon */}
      {/* {departmentsLoading && (
        <div className="bg-blue-50 border border-blue-200 rounded-md p-2 text-sm text-blue-800">
          🔄 Loading filter options...
        </div>
      )} */}

      <PageTableHeader
        isSearch
        onSearchChange={handleSearchChange}
        add
        onClickAdd={() => openModal("", actions_type.ADD_EDIT)}
        // filter
        shorting
        breadcrumbs={items}
        sortingModule="designation"
        onSortingChange={sortingUIProps?.onSortingChange}
        sortingProps={sortingUIProps}
        // filterProps={filterProps} // ✨ Just spread filterProps - that's it!
      />

      <div className={" bg-white"}>
        <CommonTable
          search={search}
          TableHeaderData={TableHeaderData}
          tabledata={tableData.data}
          currentPage={currentPage}
          totalData={totalData}
          setCurrentPage={handlePageChange}
          loading={loading}
          action={tableData.action}
        />

        <BottomDrawer
          isOpen={action === actions_type.ADD_EDIT}
          setIsOpen={handleDrawerClose}
          title={isEditValue ? "Edit Designation" : "Add Designation"}
          size="500px"
          position="right"
        >
          <CommonForm
            fields={comprehensiveFields}
            initialData={isEditValue ? isEditValue : {}}
            onSubmit={handleSubmit}
            submitButtonText="Submit"
            onCancel={handleCancel}
            cancelButtonText="Reset"
            columnsPerRow={1}
            gap="gap-4"
            rowClassName=""
            fieldClassName="bg-white p-3 rounded shadow-sm"
          />
        </BottomDrawer>
      </div>
    </div>
  );
};
export default ManageDesignation;
